import React, {useEffect, useMemo, useState} from "react";
import useIsMounted from "../../../../../../../../hooks/use-is-mounted";
import ToggleButtons from "../../../../../../../base/button/toggle_buttons";
import {Col} from "reactstrap";
import TryAgain from "../../../../../../../app-specific/try-again";
import {DataGridColumnAlignments, DataGridColumnTypes, DataGridColumnWidthTypes, DataGridDensities, useDataGridSavedState} from "../../../../../../../../../packages/data-grid";
import PartnerDataGrid from "../../../../../../../../containers/data-grid";
import {ProductTechnicalFileAssignedItemsDataGridColumnNames} from "../../../../../../../../../core/constants/table/data-grid-column-keys";

const dataGridSaveConfig = {
    name: 'AssignedItemsListCategoriesTabDataGrid',
    version: 1,
}

const AssignedItemsListCategoriesTab = ({assign, unAssign, setInAddMode, categories, searching}) => {
    const [data, setData] = useState([]);
    const isMounted = useIsMounted();

    /**
     * With each change in the categories:
     * - sets the data state with the entries of categories.
     */
    useEffect(() => {
        setData(categories?.map(e => ({
            ...e,
            togglingAssigned: false,
            assigned: true,
        })) ?? []);
    }, [categories])

    /**
     * Assigns the given category to the current technical file.
     * @param category
     * @return {Promise<void>}
     */
    const assignCategory = async (category) => {
        setData(prevState => prevState.map(e => e.id === category.id
            ? {...e, togglingAssigned: true}
            : e
        ))
        const forApi = {
            category: category
        }
        const response = await assign(forApi);
        if (!isMounted()) return;
        setData(prevState => prevState.map(e => e.id === category.id
            ? {...e, togglingAssigned: false}
            : e
        ))
        if (response?.resultFlag) {
            setData(prevState => prevState.map(e => e.id === category.id
                ? {...e, assigned: true}
                : e
            ))
        }
    }

    /**
     * Un-Assigns the given category from the current technical file.
     * @param category
     * @return {Promise<void>}
     */
    const unAssignCategory = async (category) => {
        setData(prevState => prevState.map(e => e.id === category.id
            ? {...e, togglingAssigned: true}
            : e
        ))
        const forApi = {
            category: category
        }
        const response = await unAssign(forApi);
        if (!isMounted()) return;
        setData(prevState => prevState.map(e => e.id === category.id
            ? {...e, togglingAssigned: false}
            : e
        ))
        if (response?.resultFlag) {
            setData(prevState => prevState.map(e => e.id === category.id
                ? {...e, assigned: false}
                : e
            ))
        }
    }

    const columns = useMemo(() => [
        {
            title: 'Category',
            alignment: DataGridColumnAlignments.left,
            type: DataGridColumnTypes.string,
            name: ProductTechnicalFileAssignedItemsDataGridColumnNames.category,
            sortable: false,
        },
        {
            name: ProductTechnicalFileAssignedItemsDataGridColumnNames.toggle,
            alignment: DataGridColumnAlignments.center,
            type: DataGridColumnTypes.element,
            sortable: false,
            width: {
                type: DataGridColumnWidthTypes.flex,
                size: 1,
            },
        },
    ], [])

    const [dataGridColumns] = useDataGridSavedState({
        columns: columns,
        name: dataGridSaveConfig?.name,
        version: dataGridSaveConfig?.version,
    });

    const dataGridRows = useMemo(() => data?.map(category => ({
        key: category.id,
        data: category,
        cells: {
            [ProductTechnicalFileAssignedItemsDataGridColumnNames.category]: category.title,
            [ProductTechnicalFileAssignedItemsDataGridColumnNames.toggle]:
                <ToggleButtons
                    className={'assigned-toggle-buttons'}
                    value={true}
                    onChange={(_) => category.assigned ? unAssignCategory(category) : assignCategory(category)}
                    options={[{
                        value: category.assigned,
                        name: category.togglingAssigned
                            ? "toggling..."
                            : category.assigned ? "Un-assign" : "Assign",
                        disabled: category.togglingAssigned
                    }]}
                />,
        },
    })), [data])

    return (
        <>
            <Col xs={12} className={'mt-4'}>
                {
                    !searching && !data?.length
                        ? <TryAgain
                            text={'There are no categories assigned to this document. You may assign new products' +
                                ' if you wish'}
                            buttonText={'Assign new categories'}
                            onClick={() => setInAddMode(true)}
                            showButton={true}
                        />
                        : <PartnerDataGrid
                            {...dataGridSaveConfig}
                            hideFooterActions
                            hideExporting
                            hideFooter
                            visibleRows={5}
                            classNames={{
                                toolbar: "pt-0"
                            }}
                            initialLayoutState={{
                                columns: columns,
                            }}
                            rows={dataGridRows}
                            columns={dataGridColumns}
                            state={{
                                loading: {state: searching},
                                density: DataGridDensities.compact,
                            }}
                        />
                }
            </Col>
        </>
    );
}

export default AssignedItemsListCategoriesTab;
